import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/glyph/chevron-up';

import { PortalCard } from 'view/help-center/portal-card';
import { AutoFocus } from 'view/common/auto-focus';
import { Title } from 'view/styles/help-center';
import type { PortalSummary } from 'rest/featured-and-sorted-portals';

import i18n from './messages';
import {
    Root,
    FeaturedAndSortedPortalsListContainer,
    FeaturedAndSortedPortalsListItem,
    ExploreAllButton,
    CollapseOtherButton,
    ProjectsSection,
} from './styled';
import type { SortBy } from 'rest/featured-and-sorted-portals/types';
import type { fetchFeaturedAndSortedPortalsModel } from 'state/actions/featured-and-sorted-portals';
import { ProjectCardSkeleton } from '../project-card-skeleton';

export interface Props {
    defaultViewPortals: PortalSummary[];
    expandedViewPortals: PortalSummary[];
    sortBy: SortBy;
    total: number;
    isExpandedViewLoading: boolean;
    featuredPortalsCount: number;
    isSidebarOpen: boolean;
    fetchFeaturedAndSortedPortals: typeof fetchFeaturedAndSortedPortalsModel;
}
interface State {
    expand: boolean;
}

export default class FeaturedAndSortedPortalsCardListDumb extends React.Component<Props, State> {
    state = {
        expand: false,
    };

    onLoadMoreProjects = () => {
        const { defaultViewPortals, expandedViewPortals, total } = this.props;

        this.setState({ expand: true });
        /* Only in case exploreAll has not been clicked before during in app navigation or we don't have complete data*/
        if (defaultViewPortals.length + expandedViewPortals.length < total) {
            this.props.fetchFeaturedAndSortedPortals({ sortBy: this.props.sortBy, exploreAll: true }, false);
        }
    };

    onCollapseOtherProjects = () => {
        this.setState({ expand: false });
    };

    loaderState = () => {
        return (
            <>
                {Array.from({ length: 9 }).map((_, index) => (
                    <ProjectCardSkeleton key={index} isBasic />
                ))}
            </>
        );
    };

    getFeaturedAndSortedPortalsList = (portals: PortalSummary[], shouldFocus: boolean = false) => {
        const { expand } = this.state;
        return (
            <>
                {portals.map(({ id, name, description, logoUrl }, index) => {
                    /**
                     * Have auto focus on the last card of default view section
                     */
                    const Focus = shouldFocus && index === portals.length - 1 && expand ? AutoFocus : React.Fragment;

                    return (
                        <FeaturedAndSortedPortalsListItem
                            className={this.props.isSidebarOpen ? 'sidebar-opened' : 'sidebar-closed'}
                            key={`portalCard_${id}`}
                            data-testid="portal-cards-list-item"
                        >
                            <Focus>
                                <PortalCard
                                    id={id}
                                    portalName={name}
                                    description={description}
                                    portalUrl={`/portal/${id}`}
                                    iconUrl={logoUrl}
                                />
                            </Focus>
                        </FeaturedAndSortedPortalsListItem>
                    );
                })}
            </>
        );
    };

    getMainTitle = () => {
        const { featuredPortalsCount } = this.props;
        if (featuredPortalsCount > 0) {
            return <FormattedMessage {...i18n.featuredPortalsLabel} />;
        }
        return <FormattedMessage {...i18n.remainingPortals} />;
    };

    render() {
        const {
            defaultViewPortals,
            expandedViewPortals,
            isExpandedViewLoading,
            total,
            featuredPortalsCount,
            isSidebarOpen,
        } = this.props;
        const { expand } = this.state;
        const showExploreAllButton = !expand && total > defaultViewPortals.length;
        const remainingCount = total - defaultViewPortals.length;

        return (
            <Root className={isSidebarOpen ? 'sidebar-opened' : 'sidebar-closed'} data-testid="portals-list">
                <ProjectsSection>
                    <Title data-test-id="primary-heading">{this.getMainTitle()}</Title>
                    <FeaturedAndSortedPortalsListContainer data-test-id="help-center-portal-cards">
                        {this.getFeaturedAndSortedPortalsList(defaultViewPortals, true)}
                    </FeaturedAndSortedPortalsListContainer>

                    {showExploreAllButton && (
                        <ExploreAllButton
                            id="explore-all-projects"
                            onClick={this.onLoadMoreProjects}
                            appearance="link"
                            iconAfter={<ChevronDownIcon label="chevron-down" />}
                            actionSubjectId="exploreAllProjects"
                            data-test-id="explore-all-button"
                            aria-expanded={expand}
                        >
                            <FormattedMessage {...i18n.exploreAll} values={{ remainingCount }} />
                        </ExploreAllButton>
                    )}
                </ProjectsSection>
                {expand && (
                    <ProjectsSection className={featuredPortalsCount > 0 ? 'expanded-section' : ''}>
                        {featuredPortalsCount > 0 && (
                            <Title data-test-id="secondary-heading">
                                <FormattedMessage {...i18n.remainingProjectsByName} />
                            </Title>
                        )}
                        <FeaturedAndSortedPortalsListContainer data-test-id="help-center-portal-cards">
                            {isExpandedViewLoading
                                ? this.loaderState()
                                : this.getFeaturedAndSortedPortalsList(expandedViewPortals)}
                        </FeaturedAndSortedPortalsListContainer>
                        <CollapseOtherButton
                            id="collapse-other-projects"
                            onClick={this.onCollapseOtherProjects}
                            appearance="link"
                            iconAfter={<ChevronUpIcon label="chevron-up" />}
                            actionSubjectId="collapseOtherProjects"
                            isDisabled={isExpandedViewLoading}
                            data-test-id="collapse-other-button"
                            aria-expanded={expand}
                        >
                            <FormattedMessage {...i18n.collapseOtherProjects} />
                        </CollapseOtherButton>
                    </ProjectsSection>
                )}
            </Root>
        );
    }
}
